import { useMemo } from "react";
import { Link } from "react-router";
import { useBetraegeSichtbar } from "../../../app/betraege";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { EmptyState, ErrorState, Loading } from "../../../components/States";
import { t, ta } from "../../../lib/sprache";
import { useHeute, useKooperationen, useRechnungsAnsicht } from "../kooperationen/hooks";
import { baueZeilen } from "../kooperationen/model";
import { useHubBeitraege, useVertragsLage } from "./hub-daten";
import { BLICKE, baueHubKoops, type Blick } from "./hub-model";
import { HubAnsicht } from "./HubAnsicht";

/**
 * Hub der Agentur (/agentur/hub): Marke ↔ Kampagne ↔ Kooperation ↔ Creator an einem Ort, je Kooperation der Stand von
 * Vertrag, Content und Rechnung. Sichtbar ist, was die angemeldete Person ohnehin sehen darf (RLS der Lader); Beträge zeigt
 * der Hub nicht. Im Creator-Konto (Studio) entfällt der Blickwinkel „Nach Creator“: es gibt nur einen Creator, das Konto selbst.
 */
/** Creator-Konto: ein einziger Creator, die Zeilen nennen deshalb den Deal statt des Namens */
const STUDIO = { ohneCreator: true } as const;

export function Hub() {
  const ws = useStaffWorkspace();
  const today = useHeute();
  const q = useKooperationen();
  const beitraege = useHubBeitraege();
  const vertraege = useVertragsLage();
  const rechnung = useRechnungsAnsicht(q.data?.periods, q.data?.invoices);
  const betraege = useBetraegeSichtbar();
  const selbst = !!ws.isCreator;
  const koops = useMemo(() => {
    if (!q.data) return [];
    const zeilen = baueZeilen(q.data, { today, timeZone: ws.org.timezone, selbst });
    return baueHubKoops(zeilen, {
      beitraege: beitraege.data?.beitraege ?? [], beiMarke: beitraege.data?.beiMarke ?? new Set(), periods: q.data.periods, lage: vertraege.data ?? null,
      pille: (row) => rechnung(row)?.pill ?? null, today, now: Date.now(), timeZone: ws.org.timezone, fristTage: q.data.settings?.requested_urgent_days,
      betraege, selbst,
    });
  }, [q.data, beitraege.data, vertraege.data, rechnung, today, ws.org.timezone, betraege, selbst]);

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data || beitraege.isPending || vertraege.isPending) return <Loading />;
  const blicke: readonly Blick[] = selbst ? ["marke", "kampagne"] : BLICKE;
  return (
    <HubAnsicht
      koops={koops}
      blicke={blicke}
      kartenOptionen={selbst ? STUDIO : undefined}
      suchfeld={selbst ? { label: t("Marke, Kampagne oder Deal suchen", "Search brand, campaign or deal"), platzhalter: t("Marke, Kampagne, Deal …", "Brand, campaign, deal …") } : undefined}
      titel={t("Hub", "Hub")}
      text={ta("Wie alles zusammenhängt: Marke, Kampagne, Kooperation und Creator mit dem Stand von Vertrag, Content und Rechnung.",
        "How everything connects: brand, campaign, deal and creator with the status of contract, content and invoice.",
        "Wie alles zusammenhängt: Marke, Kampagne und Deal mit dem Stand von Vertrag, Content und Rechnung.",
        "How everything connects: brand, campaign and deal with the status of contract, content and invoice.")}
      leer={(
        <EmptyState
          title={t("Noch nichts zu verbinden.", "Nothing to connect yet.")}
          text={ta("Der Hub zeigt, wie Marken, Kampagnen, Kooperationen und Creator zusammenhängen. Lege die erste Kooperation an, dann erscheint sie hier mit dem Stand von Vertrag, Content und Rechnung.",
            "The hub shows how brands, campaigns, deals and creators connect. Create your first deal and it appears here with the status of contract, content and invoice.",
            "Der Hub zeigt, wie Marken, Kampagnen und Deals zusammenhängen. Lege den ersten Deal an, dann erscheint er hier mit dem Stand von Vertrag, Content und Rechnung.",
            "The hub shows how brands, campaigns and deals connect. Create your first deal and it appears here with the status of contract, content and invoice.")}
          action={<Link className="btn primary" to="/agentur/kooperationen/neu">{ta("Kooperation anlegen", "Create deal", "Deal anlegen", "Create deal")}</Link>}
        />
      )}
      fuss={api.mode === "demo" ? <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p> : null}
    />
  );
}
